import { type FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStores } from '../hooks/useStores';
import { createUserInDb, type CreateUserPayload } from '../services/userService';

interface CreateUserForm {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  cellphone: string;
  role: 'user' | 'driver';
  car_model: string;
  number_plate: string;
  store_id: string;
}

const INITIAL: CreateUserForm = {
  email: '', password: '', first_name: '', last_name: '',
  cellphone: '', role: 'user', car_model: '', number_plate: '', store_id: '',
};

const LABEL = 'block text-sm font-medium text-stone-700';
const FIELD = 'mt-1 block w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm text-stone-900 shadow-sm outline-none transition focus:border-[#ee2b74] focus:ring-4 focus:ring-pink-100';

const CreateUser: FC = () => {
  const navigate = useNavigate();
  const { stores, fetchStores } = useStores();

  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateUserForm>(INITIAL);

  const isDriver = formData.role === 'driver';

  useEffect(() => {
    if (isDriver && stores.length === 0) void fetchStores();
  }, [isDriver, stores.length, fetchStores]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const payload: CreateUserPayload = {
        email:        formData.email.trim(),
        password:     formData.password,
        first_name:   formData.first_name.trim(),
        last_name:    formData.last_name.trim(),
        cellphone:    formData.cellphone.trim(),
        role:         formData.role,
        car_model:    formData.car_model.trim() || undefined,
        number_plate: formData.number_plate.trim().toUpperCase() || undefined,
        store_id:     formData.store_id || null,
      };
      await createUserInDb(payload);
      setFormData(INITIAL);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
      <div className="h-full w-full bg-stone-50">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold tracking-tight text-stone-900">Create User</h1>
          <p className="mt-1 text-sm text-stone-500">Add a new internal user or driver to the dashboard.</p>
        </div>

        <div className="w-full rounded-2xl border border-stone-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold text-stone-900">New account details</h2>
              <p className="mt-1 text-sm text-stone-500">Fill in the account information and choose the appropriate role.</p>
            </div>
            <div className="hidden rounded-full bg-pink-50 px-3 py-1 text-xs font-medium text-[#ee2b74] ring-1 ring-pink-200 sm:block">
              Admin Portal
            </div>
          </div>

          <div className="px-6 py-5">
            {error && (
                <div role="alert" className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Account */}
              <div className="rounded-xl border border-stone-200 bg-stone-50/60 p-4">
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-stone-900">Account details</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label htmlFor="email" className={LABEL}>Email</label>
                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className={FIELD} placeholder="name@company.com" />
                  </div>
                  <div>
                    <label htmlFor="password" className={LABEL}>Password</label>
                    <input type="password" id="password" name="password" required value={formData.password} onChange={handleChange} className={FIELD} placeholder="••••••••" />
                  </div>
                </div>
              </div>

              {/* Personal */}
              <div className="rounded-xl border border-stone-200 bg-stone-50/60 p-4">
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-stone-900">Personal details</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label htmlFor="first_name" className={LABEL}>First Name</label>
                    <input type="text" id="first_name" name="first_name" required value={formData.first_name} onChange={handleChange} className={FIELD} placeholder="Thando" />
                  </div>
                  <div>
                    <label htmlFor="last_name" className={LABEL}>Last Name</label>
                    <input type="text" id="last_name" name="last_name" required value={formData.last_name} onChange={handleChange} className={FIELD} placeholder="Mokoena" />
                  </div>
                  <div>
                    <label htmlFor="cellphone" className={LABEL}>Cellphone</label>
                    <input type="tel" id="cellphone" name="cellphone" required value={formData.cellphone} onChange={handleChange} className={FIELD} placeholder="+27 82 123 4567" />
                  </div>
                </div>
              </div>

              {/* Role */}
              <div className="rounded-xl border border-stone-200 bg-stone-50/60 p-4">
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-stone-900">Access type</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 items-end">
                  <div className="relative">
                    <label htmlFor="role" className={LABEL}>Role</label>
                    <select id="role" name="role" required value={formData.role} onChange={handleChange}
                            className={`${FIELD} pr-10 appearance-none w-full`}>
                      <option value="user">User</option>
                      <option value="driver">Driver</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 pt-6">
                      <svg className="h-4 w-4 fill-current text-stone-500" viewBox="0 0 20 20">
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                  <div className={`flex items-center h-fit rounded-xl px-4 py-3 text-sm ${
                      isDriver
                          ? 'border border-pink-200 bg-pink-50 text-[#ee2b74]'
                          : 'border border-stone-200 bg-white text-stone-600'
                  }`}>
                    {isDriver
                        ? 'Driver access enabled. Additional vehicle and store fields are required.'
                        : 'User access selected. Only core account details are needed.'}
                  </div>
                </div>
              </div>

              {/* Driver fields */}
              {isDriver && (
                  <div className="rounded-xl border border-stone-200 bg-stone-50/60 p-4">
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-stone-900">Driver details</h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="md:col-span-2">
                        <label htmlFor="store_id" className={LABEL}>Assigned Store</label>
                        <select id="store_id" name="store_id" required={isDriver} value={formData.store_id} onChange={handleChange}
                                className={`${FIELD} pr-10`}>
                          <option value="">Select a store…</option>
                          {stores.map(s => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                        {stores.length === 0 && (
                            <p className="mt-2 text-xs text-amber-600">No stores found. Please create a store first.</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="car_model" className={LABEL}>Car Model</label>
                        <input type="text" id="car_model" name="car_model" required={isDriver} value={formData.car_model} onChange={handleChange} className={FIELD} placeholder="Toyota Corolla" />
                      </div>
                      <div>
                        <label htmlFor="number_plate" className={LABEL}>Number Plate</label>
                        <input type="text" id="number_plate" name="number_plate" required={isDriver} value={formData.number_plate} onChange={handleChange} className={FIELD} placeholder="ABC 123 GP" />
                      </div>
                    </div>
                  </div>
              )}

              <div className="flex items-center justify-end gap-3 border-t border-stone-200 pt-4">
                <button type="button" disabled={loading} onClick={() => setFormData(INITIAL)}
                        className="inline-flex items-center justify-center rounded-xl border border-stone-300 bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 disabled:opacity-50">
                  Reset
                </button>
                <button type="submit" disabled={loading}
                        className="inline-flex items-center justify-center rounded-xl bg-[#ee2b74] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#d4245f] disabled:opacity-70">
                  {loading ? 'Creating…' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  );
};

export default CreateUser;